import {useMemo, useState, useEffect} from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import {PageWrapper, PageHeader, PageContentWrapper} from '../../../Layout';
import {getProgramData} from '../../../api/programs';
import {fetchNoCleaningRecordsByDate} from '../../../api/noCleaningRecords';
import {getSiteTypes} from '../../../api/siteType';
import {getResources} from '../../../api/resource';
import {getWasteTypes} from '../../../api/wasteType';
import {getWasteCategories} from '../../../api/wasteCategory';
import {getAdditionalInfo} from '../../../api/additionalInfo';
import {getSubstrateOptions} from '../../../api/substrateOptions';
import {getUsers} from '../../../api/user';
import {
    createCleanUp,
    updateCleanUp,
    updateCollectedWeight,
    updateCollectedItems,
    updateLitterBags,
    updateResources,
    updateSiteTypes,
    updateAdditionalInfo,
} from '../../../api/cleanUp';
import {IProgram} from '../../../models/program';
import {IUser, Role} from '../../../models/user';
import {ISiteType} from '../../../models/siteType';
import {IResource, ResourceType} from '../../../models/resource';
import {IWasteType} from '../../../models/wasteType';
import {IWasteCategory} from '../../../models/wasteCategory';
import {IAdditionalInfo} from '../../../models/additionalInfo';
import {ISubstrateOption} from '../../../models/substrateOption';
import Step1Program from './steps/Step1Program';
import Step2Litter from './steps/Step2BasicInfo';
import Step3Location from './steps/Step3Location';
import Step4Resources from './steps/Step4Collection';
import Step5Remarks from './steps/Step5Environmental';
import Step6Review from './steps/Step6Notes';
import StepGallery from './steps/StepGallery';
import s from './index.module.scss';

// ─── Form state types ─────────────────────────────────────────────────────────

export interface ILitterBagEntry {
    resource_id: string;
    full: string;
    half: string;
}

export interface IGeneralResourceEntry {
    resource_id: string;
    amount: string;
}

export interface ICreateCleanUpForm {
    // Step 1
    programId: string;
    date: string;
    collectorId: string;
    // Step 2 — Litter
    no_litter_found: boolean;
    collected_weights: Record<string, {wet: string; dry: string}>;
    collected_items: Record<string, Record<string, string>>;
    // Step 3 — Location
    site_types: string[];
    latitude: string;
    longitude: string;
    location_name: string;
    city_island: string;
    area: string;
    length: string;
    substrate_id: string;
    additional_location_info: string[];
    // Step 4 — Resources
    litter_bags: ILitterBagEntry[];
    general_resources: IGeneralResourceEntry[];
    // Step 5 — Remarks
    num_of_participant: string;
    notes: string;
}

// ─── Config data loaded at mount ─────────────────────────────────────────────

interface IAllConfig {
    siteTypes: ISiteType[];
    resources: IResource[];
    wasteTypes: IWasteType[];
    wasteCategories: IWasteCategory[];
    additionalInfo: IAdditionalInfo[];
    substrateOptions: ISubstrateOption[];
    users: IUser[];
}

// ─── Step icon SVG paths (viewBox 0 0 38 38) ─────────────────────────────────

const LitterIcon = (
    <svg width="18" height="18" viewBox="0 0 38 38" fill="currentColor">
        <path d="M25.4074 33.0945H12.7405C11.5871 33.1575 10.4512 32.7954 9.5541 32.0789C8.65698 31.3625 8.0629 30.3428 7.8876 29.2187L6.40288 14.5339C6.39074 14.418 6.40334 14.301 6.43986 14.1902C6.47639 14.0795 6.53604 13.9775 6.61498 13.8908C6.6943 13.8042 6.79114 13.7349 6.89927 13.6874C7.00739 13.64 7.12442 13.6153 7.24281 13.615H19.1206C19.3456 13.615 19.5614 13.7031 19.7205 13.8598C19.8796 14.0164 19.969 14.2288 19.969 14.4504C19.969 14.6719 19.8796 14.8844 19.7205 15.0411C19.5614 15.1977 19.3456 15.2857 19.1206 15.2857H8.18455L9.57595 29.0516C9.7222 29.7572 10.1236 30.3867 10.7056 30.823C11.2876 31.2593 12.0107 31.4728 12.7405 31.4239H25.4074C26.1398 31.4746 26.8659 31.2609 27.4499 30.8227C28.0339 30.3846 28.4358 29.7519 28.5804 29.0432L30.0058 15.0101C29.8959 14.8902 29.8237 14.7417 29.7978 14.5823C29.7719 14.4229 29.7935 14.2595 29.8599 14.1119C29.9263 13.9644 30.0347 13.839 30.172 13.7508C30.3093 13.6626 30.4696 13.6154 30.6336 13.615H30.9136C31.0319 13.6153 31.149 13.64 31.2571 13.6874C31.3652 13.7349 31.4621 13.8042 31.5414 13.8908C31.6218 13.9768 31.683 14.0784 31.721 14.1892C31.759 14.3 31.773 14.4174 31.762 14.5339L30.2688 29.2103C30.0934 30.3367 29.4985 31.3584 28.5997 32.0765C27.701 32.7946 26.563 33.1575 25.4074 33.0945Z" />
        <path d="M30.6336 15.3108H19.0782C18.8532 15.3108 18.6374 15.2228 18.4783 15.0661C18.3192 14.9095 18.2298 14.697 18.2298 14.4754C18.2298 14.2539 18.3192 14.0414 18.4783 13.8847C18.6374 13.7281 18.8532 13.6401 19.0782 13.6401H30.6336C30.8586 13.6401 31.0744 13.7281 31.2335 13.8847C31.3926 14.0414 31.482 14.2539 31.482 14.4754C31.482 14.697 31.3926 14.9095 31.2335 15.0661C31.0744 15.2228 30.8586 15.3108 30.6336 15.3108Z" />
        <path fillRule="evenodd" clipRule="evenodd" d="M21.9373 29.035C20.957 29.035 19.9987 28.7486 19.1838 28.2122C18.3688 27.6758 17.7337 26.9134 17.359 26.0216C16.9842 25.1297 16.8866 24.1485 17.0784 23.202C17.2703 22.2555 17.743 21.3862 18.4368 20.7043C19.1306 20.0224 20.0142 19.5585 20.9759 19.3712C21.9376 19.1839 22.9341 19.2817 23.8393 19.6522C24.7445 20.0227 25.5178 20.6493 26.0612 21.4526C26.6046 22.256 26.8938 23.1999 26.8921 24.165C26.8898 25.4574 26.3668 26.696 25.4379 27.6091C24.5089 28.5221 23.2499 29.035 21.9373 29.035Z" />
        <path d="M7.15797 12.12C6.93295 12.1388 6.70956 12.0688 6.53692 11.9255C6.36429 11.7822 6.25656 11.5772 6.23744 11.3556C6.21831 11.1341 6.28935 10.9141 6.43494 10.7441C6.58052 10.5742 6.78872 10.4682 7.01374 10.4493L30.4894 6.37302C30.6008 6.35383 30.7149 6.35643 30.8253 6.38067C30.9357 6.40492 31.0402 6.45026 31.1328 6.51425C31.2253 6.57824 31.3042 6.65957 31.3648 6.75356C31.4255 6.84756 31.4667 6.95242 31.4862 7.06211C31.5057 7.17181 31.5031 7.2842 31.4785 7.39289C31.4539 7.50158 31.4077 7.60449 31.3427 7.69563C31.2777 7.78677 31.1951 7.86432 31.0997 7.92404C31.0042 7.98375 30.8977 8.02445 30.7863 8.04364L7.3022 12.12H7.15797Z" />
        <path d="M14.0216 8.10198C13.7966 8.12081 13.5732 8.05087 13.4006 7.90753C13.228 7.7642 13.1202 7.55918 13.1011 7.33764C13.082 7.1161 13.153 6.89622 13.2986 6.72625C13.4442 6.55628 13.6524 6.45019 13.8774 6.43136L22.6415 4.91114C22.8665 4.87237 23.098 4.92313 23.2849 5.05237C23.4719 5.1816 23.599 5.3787 23.6384 5.60023C23.6778 5.82177 23.6262 6.04969 23.4949 6.23375C23.3636 6.41782 23.1635 6.54299 22.9385 6.58176L14.1744 8.10198H14.0216Z" />
    </svg>
);

const LocationIcon = (
    <svg width="18" height="18" viewBox="0 0 38 38" fill="currentColor">
        <path d="M29.1175 14.9042C29.1175 13.3071 27.8351 12.0191 26.245 12.0191C24.655 12.0191 23.3726 13.3071 23.3726 14.9042C23.3726 16.5013 24.655 17.7893 26.245 17.7893C27.8351 17.7893 29.1175 16.5013 29.1175 14.9042Z" />
        <path fillRule="evenodd" clipRule="evenodd" d="M26.245 31.6172L26.7891 30.7941C27.2214 30.077 27.7935 29.1855 28.4394 28.1789C28.6085 27.9154 28.7828 27.6438 28.9608 27.3656C29.8152 26.0305 30.7521 24.5486 31.6211 23.0657C32.4891 21.5847 33.2969 20.0898 33.8894 18.7287C34.4736 17.3867 34.8828 16.0997 34.8828 15.0587C34.8828 10.2752 31.0075 6.38281 26.245 6.38281C24.0223 6.38281 21.9928 7.23068 20.4603 8.6213C18.851 7.78564 17.0445 7.33077 15.1144 7.33077C8.54884 7.33077 3.26562 12.6888 3.26562 19.2318C3.26562 25.8264 8.60013 31.1329 15.1144 31.1329L15.0631 31.0299C18.4241 31.0299 21.4357 29.6695 23.5735 27.4348C23.7346 27.6864 23.8923 27.9323 24.046 28.1717L24.0504 28.1786C24.6964 29.1853 25.2686 30.077 25.701 30.7941L26.245 31.6172ZM26.245 7.6605C22.1851 7.6605 18.8793 10.9809 18.8793 15.0587C18.8793 15.821 19.1946 16.9039 19.7661 18.2168C20.3293 19.5105 21.1083 20.9555 21.9652 22.4176C22.8209 23.8777 23.7465 25.3421 24.5993 26.6748C24.774 26.9478 24.9459 27.2157 25.1136 27.4771C25.5223 28.1138 25.9061 28.712 26.245 29.2512C26.5839 28.712 26.9678 28.1138 27.3764 27.477L27.3771 27.4759C27.5446 27.2149 27.7163 26.9474 27.8907 26.6748C28.7435 25.3421 29.6692 23.8777 30.5249 22.4176C31.3817 20.9555 32.1607 19.5105 32.7239 18.2168C33.2954 16.9039 33.6107 15.821 33.6107 15.0587C33.6107 10.9809 30.305 7.6605 26.245 7.6605ZM15.9351 27.7326L16.6532 21.7563H13.6782L13.6269 22.3745C13.9859 22.3745 14.1911 22.5806 14.1398 22.9928L13.5756 27.7841C13.5243 28.1963 13.2678 28.4023 12.9088 28.4023L12.8575 29.0206H16.3967L16.448 28.4023C16.089 28.4023 15.9351 28.1963 15.9351 27.7841V27.7326ZM15.3196 20.5713C15.7299 20.5713 16.089 20.4168 16.3967 20.1592C16.7045 19.8501 16.9097 19.5409 16.9609 19.1288C17.0122 18.7166 16.9097 18.356 16.6532 18.0984C16.3967 17.7893 16.0377 17.6862 15.6273 17.6862C15.217 17.6862 14.8579 17.8408 14.5502 18.0984C14.2424 18.4075 14.0372 18.7166 13.9859 19.1288C13.9346 19.5409 14.0372 19.9016 14.2937 20.1592C14.5502 20.4683 14.9092 20.5713 15.3196 20.5713ZM9.06177 15.2648H15.3196C15.7812 15.2648 16.1403 14.9042 16.1403 14.4405C16.1403 13.9768 15.7812 13.6162 15.3196 13.6162H9.06177C8.60013 13.6162 8.24108 13.9768 8.24108 14.4405C8.24108 14.9042 8.60013 15.2648 9.06177 15.2648ZM9.06177 12.4312H16.6019C17.0635 12.4312 17.4226 12.0706 17.4226 11.6069C17.4226 11.1432 17.0635 10.7826 16.6019 10.7826H9.06177C8.60013 10.7826 8.24108 11.1432 8.24108 11.6069C8.24108 12.0706 8.60013 12.4312 9.06177 12.4312Z" />
    </svg>
);

const ResourcesIcon = (
    <svg width="18" height="18" viewBox="0 0 38 38" fill="currentColor">
        <path fillRule="evenodd" clipRule="evenodd" d="M31 8.5L19.5 6L7.5 8.5L2 18L7 20V27.5L19 32L31.5 27V18.48L29.5 19.36V26L20 29.5V16L23.5 22L29.5 19.36L31.5 18.48L36 16.5L31 8.5ZM19 8L12 9.5L19 11.5L26.5 9.5L19 8ZM17.5 13L8.5 10.5L4.5 17L13 20L17.5 13ZM9 26V20.5L14 22.5L18 16V29.5L9 26ZM30 10.5L20.5 13L24.5 19.36L33 15.5L30 10.5Z" />
    </svg>
);

const RemarksIcon = (
    <svg width="18" height="18" viewBox="0 0 38 38" fill="currentColor">
        <path fillRule="evenodd" clipRule="evenodd" d="M3.11719 19C3.11719 25.9982 8.78726 31.6172 15.7344 31.6172L15.6833 31.5661C21.5408 31.5661 26.5104 27.5704 27.9017 22.1451L31.1531 18.8937C31.1548 18.892 31.1565 18.8903 31.1582 18.8885L31.2061 18.8407C31.2132 18.8336 31.2201 18.8263 31.2269 18.8189L34.734 15.2617C34.9815 15.0107 34.9801 14.6069 34.7308 14.3576L29.3161 8.94296C29.0655 8.69241 28.6593 8.69241 28.4087 8.94296L25.8778 11.4739C23.5497 8.39782 19.8939 6.38281 15.7344 6.38281C8.73618 6.38281 3.11719 12.0529 3.11719 19ZM30.7489 17.4761L33.3729 14.8145L28.8624 10.304L26.2196 12.9468L30.7489 17.4761ZM25.2867 13.9308L29.794 18.4381L23.179 25.0531L17.4386 26.2862L18.6717 20.5458L25.2867 13.9308ZM8.27644 13.7897C8.27644 13.3299 8.6851 12.9213 9.14483 12.9213C9.60457 12.9213 10.0132 13.3299 10.0132 13.7897C10.0132 14.2494 9.60457 14.6581 9.14483 14.6581C8.6851 14.6581 8.27644 14.2494 8.27644 13.7897ZM8.27644 17.2632C8.27644 16.8035 8.6851 16.3948 9.14483 16.3948C9.60457 16.3948 10.0132 16.8035 10.0132 17.2632C10.0132 17.723 9.60457 18.1316 9.14483 18.1316C8.6851 18.1316 8.27644 17.723 8.27644 17.2632ZM8.27644 20.6346C8.27644 20.1238 8.6851 19.7662 9.14483 19.7662C9.60457 19.7662 10.0132 20.1749 10.0132 20.6346C10.0132 21.0944 9.60457 21.503 9.14483 21.503C8.6851 21.503 8.27644 21.0944 8.27644 20.6346ZM15.4279 21.4519H11.9032C11.4435 21.4519 11.0349 21.0433 11.0349 20.5835C11.0349 20.0727 11.4435 19.7151 11.9032 19.7151H15.4279C15.8876 19.7151 16.2963 20.1238 16.2963 20.5835C16.2963 21.0433 15.8876 21.4519 15.4279 21.4519ZM17.6755 18.0805H11.9032C11.4435 18.0805 11.0349 17.6719 11.0349 17.2121C11.0349 16.7524 11.4435 16.3438 11.9032 16.3438H17.6755C18.1352 16.3438 18.5439 16.7524 18.5439 17.2121C18.5439 17.6719 18.1352 18.0805 17.6755 18.0805ZM19.9231 14.7091H11.9032C11.4435 14.7091 11.0349 14.3005 11.0349 13.8407C11.0349 13.3299 11.4435 12.9724 11.9032 12.9724H19.9231C20.3828 12.9724 20.7915 13.381 20.7915 13.8407C20.7915 14.3005 20.3828 14.7091 19.9231 14.7091Z" />
    </svg>
);

const GalleryIcon = (
    <svg width="18" height="18" viewBox="0 0 38 38" fill="currentColor">
        <path fillRule="evenodd" clipRule="evenodd" d="M6 8a2 2 0 0 0-2 2v18a2 2 0 0 0 2 2h26a2 2 0 0 0 2-2V10a2 2 0 0 0-2-2H6zm0 2h26v18H6V10zm4 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6zm-1 3a1 1 0 1 1 2 0 1 1 0 0 1-2 0zm-1 9 4-5 3 4 4-6 6 7H8z" />
    </svg>
);

// ─── Stepper config ───────────────────────────────────────────────────────────

const STEPS: {label: string; description: string; icon?: JSX.Element}[] = [
    {label: 'Program', description: 'Program & date'},
    {label: 'Litter', description: 'Waste collected', icon: LitterIcon},
    {label: 'Location', description: 'Site & coordinates', icon: LocationIcon},
    {label: 'Resources', description: 'Bags & tools', icon: ResourcesIcon},
    {label: 'Remarks', description: 'Activity & notes', icon: RemarksIcon},
    {label: 'Review', description: 'Summary & submit'},
    {label: 'Gallery', description: 'Before & after photos', icon: GalleryIcon},
];

const REVIEW_STEP = 5;
const GALLERY_STEP = 6;

const INITIAL_FORM: ICreateCleanUpForm = {
    programId: '',
    date: '',
    collectorId: '',
    no_litter_found: false,
    collected_weights: {},
    collected_items: {},
    site_types: [],
    latitude: '',
    longitude: '',
    location_name: '',
    city_island: '',
    area: '',
    length: '',
    substrate_id: '',
    additional_location_info: [],
    litter_bags: [],
    general_resources: [],
    num_of_participant: '',
    notes: '',
};

// ─── Component ────────────────────────────────────────────────────────────────

export const CleanUpCreate = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const returnDate = (location.state as {returnDate?: string} | null)?.returnDate;
    const [currentStep, setCurrentStep] = useState(0);
    const [form, setForm] = useState<ICreateCleanUpForm>(INITIAL_FORM);

    const [programs, setPrograms] = useState<IProgram[]>([]);
    const [allConfig, setAllConfig] = useState<IAllConfig | null>(null);
    const [configLoading, setConfigLoading] = useState(true);

    const [noCleaningConflict, setNoCleaningConflict] = useState<string | null>(null);

    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [showStepErrors, setShowStepErrors] = useState(false);
    const [createdId, setCreatedId] = useState<string | null>(null);

    // Load all data once on mount
    useEffect(() => {
        Promise.all([
            getProgramData(),
            getSiteTypes(),
            getResources(),
            getWasteTypes(),
            getWasteCategories(),
            getAdditionalInfo(),
            getSubstrateOptions(),
            getUsers(),
        ])
            .then(
                ([
                    progs,
                    siteTypes,
                    resources,
                    wasteTypes,
                    wasteCategories,
                    additionalInfo,
                    substrateOptions,
                    users,
                ]) => {
                    setPrograms(progs);
                    setAllConfig({
                        siteTypes,
                        resources,
                        wasteTypes,
                        wasteCategories,
                        additionalInfo,
                        substrateOptions,
                        users,
                    });
                }
            )
            .catch(console.error)
            .finally(() => setConfigLoading(false));
    }, []);

    // Filter config data by selected program
    const programScopedData = useMemo(() => {
        if (!form.programId || !allConfig) return null;
        const id = form.programId;
        const program = programs.find((p) => p.id === id);
        return {
            collectors: allConfig.users.filter(
                (u) => u.role === Role.collector && program?.users.includes(u.id)
            ),
            siteTypes: allConfig.siteTypes,
            litterBags: allConfig.resources.filter(
                (r) => r.programs.includes(id) && r.type === ResourceType.LITTER_BAG
            ),
            generalResources: allConfig.resources.filter(
                (r) => r.programs.includes(id) && r.type === ResourceType.GENERAL
            ),
            wasteTypes: allConfig.wasteTypes.filter((wt) => wt.programs.includes(id)),
            wasteCategories: allConfig.wasteCategories,
            additionalInfo: allConfig.additionalInfo.filter((ai) => ai.programs.includes(id)),
            substrateOptions: allConfig.substrateOptions.filter((so) =>
                so.programs.includes(id)
            ),
        };
    }, [form.programId, programs, allConfig]);

    // Reset program-dependent fields when program changes
    useEffect(() => {
        setForm((prev) => ({
            ...prev,
            collectorId: '',
            substrate_id: '',
            collected_weights: {},
            collected_items: {},
            litter_bags: [],
            general_resources: [],
            site_types: [],
            additional_location_info: [],
        }));
    }, [form.programId]);

    // Check for NoCleaningRecord conflict as soon as program + date are both set
    useEffect(() => {
        if (!form.programId || !form.date) {
            setNoCleaningConflict(null);
            return;
        }
        fetchNoCleaningRecordsByDate(form.date)
            .then((records) => {
                const conflict = records.find((r) => r.program === form.programId);
                setNoCleaningConflict(conflict ? conflict.reason : null);
            })
            .catch(() => setNoCleaningConflict(null));
    }, [form.programId, form.date]);

    const updateForm = (updates: Partial<ICreateCleanUpForm>) =>
        setForm((prev) => ({...prev, ...updates}));

    const canGoNext = () => {
        if (currentStep === 0)
            return !!form.programId && !!form.date && !!form.collectorId;
        if (currentStep === 2)
            return !!form.latitude && !!form.longitude;
        return true;
    };

    const canSubmit = () => !!form.latitude && !!form.longitude && !submitting;

    const handleNext = () => {
        if (!canGoNext()) {
            setShowStepErrors(true);
            return;
        }
        setShowStepErrors(false);
        if (currentStep < STEPS.length - 1) setCurrentStep((s) => s + 1);
    };

    const handleBack = () => {
        setShowStepErrors(false);
        if (currentStep > 0) setCurrentStep((s) => s - 1);
    };

    const handleSubmit = async () => {
        if (!canSubmit()) return;
        setSubmitting(true);
        setSubmitError(null);

        try {
            // 1. Create the clean-up
            const created = await createCleanUp({
                programId: form.programId,
                date: form.date,
                collectorId: form.collectorId,
                location: {
                    latitude: parseFloat(form.latitude),
                    longitude: parseFloat(form.longitude),
                },
                area: form.area ? parseFloat(form.area) : undefined,
                length: form.length ? parseFloat(form.length) : undefined,
            });

            const id = created.id;

            // 2. Build patch payload for general fields
            const patchDto: Record<string, unknown> = {};
            if (form.num_of_participant)
                patchDto.num_of_participant = parseInt(form.num_of_participant);
            if (form.location_name) patchDto.location_name = form.location_name;
            if (form.notes) patchDto.notes = form.notes;
            if (form.city_island) patchDto.city_island = form.city_island;
            if (form.substrate_id) patchDto.substrate_id = form.substrate_id;

            // 3. Run all sub-resource updates sequentially after create.
            // These endpoints each read-modify-write the whole clean-up row,
            // so firing them concurrently races and can silently drop fields
            // (e.g. location_name/city_island) — must run one at a time.
            const updates: (() => Promise<unknown>)[] = [];

            if (Object.keys(patchDto).length > 0) {
                updates.push(() => updateCleanUp(id, patchDto));
            }

            updates.push(() =>
                updateCollectedWeight(id, {
                    no_litter_found: form.no_litter_found,
                    collected_weight: form.no_litter_found
                        ? []
                        : Object.entries(form.collected_weights)
                              .filter(([, v]) => v.wet || v.dry)
                              .map(([wasteTypeId, v]) => ({
                                  waste_type: wasteTypeId,
                                  dry: v.dry ? parseInt(v.dry) : undefined,
                                  wet: v.wet ? parseInt(v.wet) : undefined,
                              })),
                })
            );

            Object.entries(form.collected_items).forEach(([wasteCategoryId, items]) => {
                const filledItems = Object.entries(items)
                    .filter(([, amount]) => amount && parseInt(amount) > 0)
                    .map(([itemId, amount]) => ({
                        waste_category_item: itemId,
                        amount: parseInt(amount),
                    }));
                if (filledItems.length > 0) {
                    updates.push(() =>
                        updateCollectedItems(id, {
                            waste_category: wasteCategoryId,
                            items: filledItems,
                        })
                    );
                }
            });

            if (form.litter_bags.length > 0) {
                updates.push(() =>
                    updateLitterBags(id, {
                        resources: form.litter_bags.map((lb) => ({
                            resource_id: lb.resource_id,
                            full: lb.full ? parseInt(lb.full) : undefined,
                            half: lb.half ? parseInt(lb.half) : undefined,
                        })),
                    })
                );
            }

            if (form.general_resources.length > 0) {
                updates.push(() =>
                    updateResources(id, {
                        resources: form.general_resources.map((gr) => ({
                            resource_id: gr.resource_id,
                            amount: parseInt(gr.amount) || 0,
                        })),
                    })
                );
            }

            if (form.site_types.length > 0) {
                updates.push(() => updateSiteTypes(id, {site_types: form.site_types}));
            }

            if (form.additional_location_info.length > 0) {
                updates.push(() =>
                    updateAdditionalInfo(id, {location_infos: form.additional_location_info})
                );
            }

            const failed: unknown[] = [];
            for (const runUpdate of updates) {
                try {
                    await runUpdate();
                } catch (err) {
                    failed.push(err);
                }
            }
            if (failed.length > 0) {
                setSubmitError(
                    `Clean-up was created, but ${failed.length} update(s) failed to save. You can edit the record to correct missing data.`
                );
            }
            setCreatedId(id);
            setCurrentStep(GALLERY_STEP);
        } catch (err: unknown) {
            const msg =
                (err as {response?: {data?: {message?: string}}})?.response?.data?.message;
            setSubmitError(msg || 'Failed to create clean-up. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const renderStep = () => {
        switch (currentStep) {
            case 0:
                return (
                    <Step1Program
                        form={form}
                        programs={programs}
                        collectors={programScopedData?.collectors ?? []}
                        noCleaningConflict={noCleaningConflict}
                        onChange={updateForm}
                    />
                );
            case 1:
                return (
                    <Step2Litter
                        form={form}
                        wasteTypes={programScopedData?.wasteTypes ?? []}
                        wasteCategories={programScopedData?.wasteCategories ?? []}
                        onChange={updateForm}
                    />
                );
            case 2:
                return (
                    <Step3Location
                        form={form}
                        substrateOptions={programScopedData?.substrateOptions ?? []}
                        siteTypes={programScopedData?.siteTypes ?? []}
                        additionalInfo={programScopedData?.additionalInfo ?? []}
                        onChange={updateForm}
                        showErrors={showStepErrors}
                    />
                );
            case 3:
                return (
                    <Step4Resources
                        form={form}
                        litterBags={programScopedData?.litterBags ?? []}
                        generalResources={programScopedData?.generalResources ?? []}
                        onChange={updateForm}
                    />
                );
            case 4:
                return (
                    <Step5Remarks form={form} onChange={updateForm} />
                );
            case 5:
                return (
                    <Step6Review
                        form={form}
                        programs={programs}
                        submitError={submitError}
                    />
                );
            case 6:
                return (
                    <>
                        {submitError && (
                            <div className={s.errorBanner}>{submitError}</div>
                        )}
                        <StepGallery cleanUpId={createdId!} initialSpots={[]} />
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <PageWrapper>
            <PageHeader title="Create New Clean-up" />
            <PageContentWrapper>
                <div className={s.container}>
                    {configLoading ? (
                        <div className={s.loadingState}>Loading configuration data...</div>
                    ) : (
                        <>
                            {/* Stepper */}
                            <div className={s.stepper}>
                                {STEPS.map((step, index) => {
                                    const isClickable =
                                        currentStep !== GALLERY_STEP && index < currentStep && !submitting;
                                    return (
                                    <div
                                        key={index}
                                        className={[
                                            s.stepItem,
                                            index === currentStep ? s.active : '',
                                            index < currentStep ? s.completed : '',
                                            isClickable ? s.clickable : '',
                                        ]
                                            .filter(Boolean)
                                            .join(' ')}
                                        onClick={isClickable ? () => { setCurrentStep(index); setShowStepErrors(false); } : undefined}
                                    >
                                        <div className={s.stepIndicator}>
                                            {index < currentStep ? (
                                                '✓'
                                            ) : step.icon ? (
                                                step.icon
                                            ) : (
                                                index + 1
                                            )}
                                        </div>
                                        <div className={s.stepLabels}>
                                            <span className={s.stepTitle}>{step.label}</span>
                                            <span className={s.stepDesc}>
                                                {step.description}
                                            </span>
                                        </div>
                                    </div>
                                );
                                })}
                            </div>

                            {/* Step content */}
                            <div className={s.stepCard}>{renderStep()}</div>

                            {/* Navigation */}
                            <div className={s.navigation}>
                                {currentStep !== GALLERY_STEP && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const d = returnDate ?? (form.date ? form.date.slice(0, 7) : undefined);
                                            navigate(`/clean-ups${d ? `?date=${d}` : ''}`);
                                        }}
                                        className={s.cancelButton}
                                    >
                                        Cancel
                                    </button>
                                )}
                                <div className={s.navSpacer} />
                                {currentStep > 0 && currentStep !== GALLERY_STEP && (
                                    <button
                                        type="button"
                                        onClick={handleBack}
                                        className={s.backButton}
                                    >
                                        Back
                                    </button>
                                )}
                                {currentStep === GALLERY_STEP ? (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const d = returnDate ?? (form.date ? form.date.slice(0, 7) : undefined);
                                            navigate(`/clean-ups${d ? `?date=${d}` : ''}`);
                                        }}
                                        className={s.submitButton}
                                    >
                                        Done
                                    </button>
                                ) : currentStep === REVIEW_STEP ? (
                                    <>
                                        {(!form.latitude || !form.longitude) && (
                                            <span className={s.coordWarning}>
                                                Coordinates are required to submit
                                            </span>
                                        )}
                                        <button
                                            type="button"
                                            onClick={handleSubmit}
                                            className={s.submitButton}
                                            disabled={!canSubmit()}
                                        >
                                            {submitting ? 'Creating...' : 'Create Clean-up'}
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={handleNext}
                                        className={s.nextButton}
                                        disabled={!canGoNext()}
                                    >
                                        Next
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </PageContentWrapper>
        </PageWrapper>
    );
};
